import { useTranslation } from "react-i18next";
import ApiService from "../../../core/service/api-service";
import {
    DATE_OPTIONS,
    getBeginLocalStr,
    getBeginStr,
    getDateFromNow,
    getEndLocalStr,
    getEndStr
} from "../../../core/utils/date-utils";
import {Button, Collapse, ConfigProvider, Divider, Form, Input, Radio, Select, Space, Switch, Typography} from "antd";
import DatePicker from "../../../core/components/DatePicker";
import { useIsSmallScreen } from "../../../core/utils/useMediaQuery";
import { DatePickerFooter } from "../../../core/components/DatePickerFooter";
import SearchableTreeView from "../../../core/components/SearchableTreeView";
import { useEffect, useRef, useState } from "react";
import ModalitySelectBox from "../../modality/ModalitySelectBox";
import HospitalTreeView from "../../hospital/HospitalTreeView";
import { useDataGrid } from "../../../core/components/data-grid/DataGridContext";
import QuickDatePicker from "../../../core/components/data-grid/QuickDatePicker";
import { PERMISSIONS, useUser } from "../../../core/context/UserContext";
import HospitalGroupSelectBox from "../../hospital-group/HospitalGroupSelectBox";
import StudyListSelect from "../../study-list/StudyListSelect";
import { t } from "i18next";
import UserSelectBox from "../../user/UserSelectBox";
import { buildDefaultStudyFilter, QUICK_FILTER_RESET, quickFilters } from "./study-quick-filters";
import HospitalSelectBox from "../../hospital/HospitalSelectBox";
import { LocalStorageService } from "../../../core/service/local-storage-service";
import InstitutionSelect from "../../user/InstitutionSelect";
import "../../../core/style/grid-filter.css";
import { matchesSearch } from "../../../core/utils/text-search";
import ResponsiveSelect from "../../../core/components/ResponsiveSelect";
const { RangePicker } = DatePicker;

// Split a list field on comma OR whitespace and trim, dropping empties.
// Zaten dizi olan değeri de kabul eder: kaydedilmiş filtre geri yüklenirken bu alanlar dizi olarak
// gelebiliyor ve string varsayımı çalışma zamanında patlıyordu.
const splitList = (v) => {
    if (Array.isArray(v)) return v.map(s => `${s}`.trim()).filter(Boolean);
    return `${v ?? ''}`.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
};

// Fields that live inside the collapsed "Detail Filters" section. If any is
// active we auto-expand it so the user notices the applied filter.
const DETAIL_FIELDS = [
    'institutions', 'idList', 'studyInstanceUIDList', 'studyTypeList', 'studyStatus',
    'assignDoctor', 'assignReporter', 'readUserId', 'writeUserId', 'isReportWritten',
    'hasVoiceRecord', 'isRead', 'isReportable', 'hasOpenTask', 'hasAiDicom', 'isDeleted',
    'hl7FilterType', 'listIdList',
];

// Whether a value counts as an active (non-default, non-"All") filter.
const isValueActive = (key, v, defaults) => {
    if (v === null || v === undefined || v === '') return false;
    if (Array.isArray(v)) return v.filter(x => x !== null && x !== undefined && x !== -1).length > 0;
    if (v === -1) return false;
    if (defaults && Object.prototype.hasOwnProperty.call(defaults, key) && defaults[key] === v) return false;
    return true;
};

const hasActiveDetailFilter = (vals, defaults) =>
    DETAIL_FIELDS.some(k => isValueActive(k, vals?.[k], defaults));

// A field is "filled" (highlight it) if it has any value — even a default.
const isFilled = (v) => {
    if (v === null || v === undefined || v === '') return false;
    if (Array.isArray(v)) return v.length > 0;
    return true;
};

// Hazır filtre tanımları ve varsayılan filtre grid üstündeki hızlı filtre çubuğuyla ortak (bkz. study-quick-filters).
const getDefaultFilter = () => window.studyFilter || buildDefaultStudyFilter()

export default function StudyFilter() {
    const [dateType, setDateType] = useState('received')
    const defaultFilter = useRef(getDefaultFilter());
    const [writableUsers, setWritableUsers] = useState([])
    const [readableUsers, setReadableUsers] = useState([])

    const [showHospitalFilter, setShowHospitalFilter] = useState(true)
    const { hasPermission, user } = useUser();
    const [showDetail, setShowDetail] = useState(false)

    const { onFilterPageSort, queryModel } = useDataGrid();
    const [dates, setDates] = useState({
        dateTimeBegin: defaultFilter.current.studyReceivedDateTimeBegin,
        dateTimeEnd: defaultFilter.current.studyReceivedDateTimeEnd,
    })

    const { t } = useTranslation();
    const formRef = useRef();
    const isMobile = useIsSmallScreen(992);

    // On mobile the filter lives inside a left Drawer; render every dropdown into
    // the scrollable drawer body so it can be scrolled above the on-screen
    // keyboard instead of being fixed to <body> (where the keyboard covers it).
    const mobilePopupContainer = (node) =>
        node?.closest?.('.ant-drawer-body') || node?.parentElement || document.body;

    // Track form values so we can highlight every filled filter input.
    const [formValues, setFormValues] = useState({});
    // searchText has its own (box) highlight; keep its input white.
    const activeFields = Object.keys(formValues).filter(k => isFilled(formValues[k]) && k !== 'searchText');
    const highlightCss = activeFields.map(f => {
        const base = `.grid-filter-form .ant-form-item:has([id="${f}"])`;
        // Never highlight a disabled control.
        return `${base} .ant-form-item-label>label{color:var(--accent-dark);font-weight:600}`
            + `${base} .ant-input:not([disabled]),`
            + `${base} .ant-select:not(.ant-select-disabled) .ant-select-selector,`
            + `${base} .ant-picker:not(.ant-picker-disabled),`
            + `${base} .ant-input-affix-wrapper:not(.ant-input-affix-wrapper-disabled){border-color:var(--accent) !important;background:var(--accent-soft) !important}`;
    }).join('');

    // SearchText değerini izle
    const [searchText, setSearchText] = useState(defaultFilter.current.searchText || '');
    const searchActive = !!(searchText && searchText.length > 0);
    // Arama kapsamı "seçili tarih aralığı" ise (çubuktan seçilir) arama doluyken de tarihler uygulanır → kilitlenmez.
    const datesLocked = searchActive && formValues.searchInDateRange !== true;

    // Highlight each date box on its OWN value; skip while disabled (search active).
    const dateStyle = (v) => (v && !datesLocked)
        ? { borderColor: 'var(--accent)', background: 'var(--accent-soft)' }
        : undefined;

    // Trim on paste (frontend, not backend). For list fields normalize the whole
    // value; for scalar fields just trim the pasted text.
    // Yapıştırma seçili metnin YERİNE geçer: input'un içini seçip yapıştırınca
    // eski değer korunup listeye ekleniyordu.
    const handlePaste = (fieldName, isList) => (e) => {
        const pasted = (e.clipboardData || window.clipboardData)?.getData('text');
        if (pasted == null) return;
        e.preventDefault();
        const current = `${formRef.current.getFieldValue(fieldName) ?? ''}`;
        const start = e.target.selectionStart ?? current.length;
        const end = e.target.selectionEnd ?? current.length;
        const head = current.slice(0, start);
        const tail = current.slice(end);
        // Liste alanında sona eklerken ayırıcı koy ki arka arkaya yapıştırılan
        // id'ler tek bir jetona yapışmasın.
        const sep = (isList && !tail && head && !/[\s,]$/.test(head)) ? ', ' : '';
        const merged = `${head}${sep}${pasted}${tail}`;
        const next = isList
            ? splitList(merged).join(', ')
            : merged.trim();
        formRef.current.setFieldsValue({ [fieldName]: next });
        setFormValues(formRef.current.getFieldsValue(true));
    };

    const filter = async (form, dates) => {
        let filter = {
            ...form,
            patientName: form.patientName?.trim(),
            accessionNumber: form.accessionNumber?.trim(),
            idList: splitList(form.idList),
            studyInstanceUIDList: splitList(form.studyInstanceUIDList),
            patientIdList: splitList(form.patientId),
        };

        if (dateType === 'received') {
            filter = {
                ...filter,
                studyReceivedDateTimeBegin: dates.dateTimeBegin,
                studyReceivedDateTimeEnd: dates.dateTimeEnd,
            }
        }
        else if (dateType === 'study') {
            filter = {
                ...filter,
                studyDateTimeBegin: dates.dateTimeBegin,
                studyDateTimeEnd: dates.dateTimeEnd,
            }
        }

        window.studyFilter = filter;

        onFilterPageSort({
            filter
        })

    }

    const reset = async () => {
        window.location.reload()
    }

    const getUsers = async () => {
        if (hasPermission(PERMISSIONS.STUDY_ASSIGN_DOCTOR_USER)) {
            const readable = await ApiService.UserManager.GetReadableUsers();
            if (readable.success) {
                setReadableUsers(readable.data);
            }
        }

        if (hasPermission(PERMISSIONS.STUDY_ASSIGN_REPORTER_USER)) {
            const writable = await ApiService.UserManager.GetWritableUsers();
            if (writable.success) {
                setWritableUsers(writable.data);
            }
        }
    }

    // Formu UYGULANAN filtreyle eşitler. initialValues yalnız mount'ta uygulanır; ayrıca filtre yalnız bu
    // panelden değil grid üstündeki hızlı filtre çubuğundan da (arama dahil) uygulanıyor (bkz. StudyQuickFilterBar).
    // Eşitlenmezse paneldeki bir sonraki "Ara" oradan yapılan seçimi sessizce geri alır; oradan yapılan
    // aramada paneldeki tarih alanları da yanlışlıkla açık kalırdı.
    const syncFormFromFilter = (saved) => {
        if (!saved || !formRef.current) return;

        // Uygulanan filtrede OLMAYAN alan formda da kalmamalı (ör. çubuktan "Sıfırla"): önce hepsini boşalt.
        const cleared = Object.fromEntries(
            Object.keys(formRef.current.getFieldsValue(true)).map(k => [k, undefined])
        );

        // Uygulanan filtre İŞLENMİŞ haldedir: filter() bu üç alanı splitList ile diziye
        // çevirmiştir. Form alanları ise metin kutusu — üçünü de string'e geri çevirmek gerekir.
        // studyInstanceUIDList atlanmıştı: sayfadan çıkıp geri dönünce dizi olarak forma yazılıyor,
        // sonraki aramada splitList bir diziye .split() çağırıp patlıyordu.
        formRef.current.setFieldsValue({
            ...cleared,
            ...saved,
            idList: Array.isArray(saved.idList) ? saved.idList.join(',') : saved.idList,
            studyInstanceUIDList: Array.isArray(saved.studyInstanceUIDList)
                ? saved.studyInstanceUIDList.join(',')
                : (saved.studyInstanceUIDList ?? ''),
            patientId: Array.isArray(saved.patientIdList)
                ? saved.patientIdList.join(',')
                : (saved.patientId ?? ''),
        });

        setSearchText(saved.searchText || '');

        if (saved.studyDateTimeBegin || saved.studyDateTimeEnd) {
            setDateType('study');
            setDates({
                dateTimeBegin: saved.studyDateTimeBegin,
                dateTimeEnd: saved.studyDateTimeEnd,
            });
        } else {
            setDateType('received');
            setDates({
                dateTimeBegin: saved.studyReceivedDateTimeBegin,
                dateTimeEnd: saved.studyReceivedDateTimeEnd,
            });
        }
    };

    useEffect(() => {
        // ileri/geri vb. durumlarda grid filtreli yüklenip form boş kalıyordu → window.studyFilter'dan eşitle.
        const saved = window.studyFilter;

        // A detail filter is active but hidden → open the section so the user sees it.
        if (hasActiveDetailFilter(saved || defaultFilter.current, defaultFilter.current)) {
            setShowDetail(true);
        }

        syncFormFromFilter(saved);

        // Seed tracked values (incl. defaults) so filled inputs highlight on load.
        if (formRef.current) {
            setFormValues(formRef.current.getFieldsValue(true));
        }

        onFilterPageSort({
            filter: { ...defaultFilter.current }
        })
    }, [])

    useEffect(() => {
        getUsers()
    }, []);

    // Filtre nesnesi yalnız bir filtre UYGULANDIĞINDA yenilenir (sayfa/sıralama/SignalR tazelemesi aynı
    // nesneyi korur); her uygulamada formu eşitle — kaynağı bu panel ya da hızlı filtre çubuğu.
    const appliedFilter = queryModel?.filter;
    useEffect(() => {
        if (!appliedFilter || Object.keys(appliedFilter).length === 0) return;

        syncFormFromFilter(appliedFilter);
        setFormValues(formRef.current?.getFieldsValue(true) || {});
    }, [appliedFilter]);

    return (
        <ConfigProvider getPopupContainer={isMobile ? mobilePopupContainer : undefined}>
            {highlightCss && <style>{highlightCss}</style>}
            <Form
                ref={formRef}
                className={'grid-filter-form'}
                layout={'vertical'}
                onFinish={(form) => filter(form, dates)}
                onValuesChange={(_, all) => setFormValues(all)}
                initialValues={defaultFilter.current}
            >
                <div style={{
                    background: searchActive ? 'var(--accent-soft)' : 'var(--surface-2)',
                    padding: '8px',
                    borderRadius: 6,
                    marginBottom: 8,
                    border: searchActive ? '1px solid var(--accent)' : '1px solid var(--border)'
                }}>
                    <div style={{
                        marginBottom: 4,
                        fontSize: '11px',
                        color: searchActive ? 'var(--accent-dark)' : 'var(--text-2)',
                        fontStyle: 'italic'
                    }}>
                        {t('Search by patient name, ID, accession number or description - searches all dates')}
                    </div>
                    <Form.Item
                        name='searchText'
                        style={{ marginBottom: 0 }}
                    >
                        <Input.Search
                            placeholder={t('Type anything: name, ID, number...')}
                            allowClear
                            enterButton={t('Search')}
                            // Enter'da tarayıcı formu kendiliğinden submit eder (formda submit tipli
                            // "Search" butonu var); antd ayrıca onSearch'ü çağırır → grid iki kez sorgulanırdı.
                            // Native submit'i keserek tek yol bırakıyoruz: onSearch → form.submit().
                            onPressEnter={(e) => e.preventDefault()}
                            onSearch={() => {
                                formRef.current.submit();
                            }}
                            size="small"
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </Form.Item>
                    {/* Arama kapsamı yalnız hızlı filtre çubuğundan seçilir; gizli alan panelden "Ara" deyince düşmesin diye. */}
                    <Form.Item name={'searchInDateRange'} hidden>
                        <Input />
                    </Form.Item>
                </div>

                <Space style={{ marginBottom: 16 }}>
                    <Button type={'primary'} size={'small'} htmlType='submit'>
                        {t('Search')}
                    </Button>
                    <Button size={'small'} onClick={reset}>
                        {t('Reset')}
                    </Button>
                </Space>

                <Form.Item
                    label={(
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Radio.Group
                                disabled={datesLocked}
                                options={[{ label: t('Received Date'), value: 'received' }, { label: t('Study Date'), value: 'study' },]}
                                onChange={(e) => setDateType(e.target.value)}
                                value={dateType}
                            />
                        </div>
                    )}
                    style={{ margin: 0, padding: 0 }}
                >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <DatePicker
                            disabled={datesLocked}
                            locale={LocalStorageService.getAntLocale()}
                            value={dates.dateTimeBegin}
                            showToday={false}
                            style={dateStyle(dates.dateTimeBegin)}
                            onChange={(date) => {
                                setDates({
                                    ...dates,
                                    dateTimeBegin: date,
                                });
                            }}
                        />
                        <DatePicker
                            disabled={datesLocked}
                            locale={LocalStorageService.getAntLocale()}
                            value={dates.dateTimeEnd}
                            showToday={false}
                            style={dateStyle(dates.dateTimeEnd)}
                            onChange={(date) => {
                                setDates({
                                    ...dates,
                                    dateTimeEnd: date,
                                });
                            }}
                        />
                    </div>
                </Form.Item>

                <Form.Item>
                    <QuickDatePicker
                        disabled={datesLocked}
                        onChange={(start, end) => {
                            setDates({
                                dateTimeBegin: start,
                                dateTimeEnd: end
                            });
                        }}
                    />
                </Form.Item>

                {/*<Form.Item*/}
                {/*    label={t('Received Date')}*/}
                {/*    style={{ margin: 0, padding: 0 }}*/}
                {/*>*/}
                {/*    <div style={{ display: 'flex', alignItems: 'center' }}>*/}
                {/*        <DatePicker*/}
                {/*            locale={LocalStorageService.getAntLocale()}*/}
                {/*            value={dates.studyReceivedDateTimeBegin}*/}
                {/*            showToday={false}*/}
                {/*            onChange={(date) => {*/}
                {/*                setDates({*/}
                {/*                    ...dates,*/}
                {/*                    studyReceivedDateTimeBegin: date,*/}
                {/*                    studyDateTimeBegin: null,*/}
                {/*                    studyDateTimeEnd: null,*/}
                {/*                });*/}
                {/*            }}*/}
                {/*        />*/}
                {/*        <DatePicker*/}
                {/*            locale={LocalStorageService.getAntLocale()}*/}
                {/*            value={dates.studyReceivedDateTimeEnd}*/}
                {/*            showToday={false}*/}
                {/*            onChange={(date) => {*/}
                {/*                setDates({*/}
                {/*                    ...dates,*/}
                {/*                    studyReceivedDateTimeEnd: date,*/}
                {/*                    studyDateTimeBegin: null,*/}
                {/*                    studyDateTimeEnd: null,*/}
                {/*                });*/}
                {/*            }}*/}
                {/*        />*/}
                {/*    </div>*/}
                {/*</Form.Item>*/}

                {/*<Form.Item>*/}
                {/*    <QuickDatePicker*/}
                {/*        onChange={(start, end) => {*/}
                {/*            setDates({*/}
                {/*                studyDateTimeBegin: null,*/}
                {/*                studyDateTimeEnd: null,*/}
                {/*                studyReceivedDateTimeBegin: start,*/}
                {/*                studyReceivedDateTimeEnd: end,*/}
                {/*            });*/}
                {/*        }}*/}
                {/*    />*/}
                {/*</Form.Item>*/}

                {hasPermission(PERMISSIONS.HOSPITAL_GROUP_FILTER) && (
                    <Form.Item
                        label={t('Hospital Groups')}
                        name='hospitalGroupIdList'
                    >
                        <HospitalGroupSelectBox
                            modalOnMobile
                            modalTitle={t('Hospital Groups')}
                        />
                    </Form.Item>
                )}

                <Form.Item label={t('Quick Filters')} name='quickFilters'>
                    <ResponsiveSelect
                        modalOnMobile
                        modalTitle={t('Quick Filters')}
                        options={quickFilters(user?.data?.id).map((x) => ({
                            value: x.label,
                            label: t(x.label),
                        }))}
                        placeholder={t('Quick Filters')}
                        onClear={() => {
                            formRef.current.setFieldsValue({ ...QUICK_FILTER_RESET });
                            setFormValues(formRef.current.getFieldsValue(true));
                        }}
                        allowClear={true}
                        onChange={(value) => {
                            const quick = quickFilters(user?.data?.id).find(
                                (x) => x.label === value
                            );
                            if (quick) {
                                formRef.current.setFieldsValue({
                                    ...QUICK_FILTER_RESET,
                                    ...quick.filters,
                                });
                                setFormValues(formRef.current.getFieldsValue(true));
                            }
                        }}
                    />
                </Form.Item>

                <Form.Item label={t('Patient Id')} name='patientId'>
                    <Input
                        placeholder={t('Separate values with comma, space or new line')}
                        onPaste={handlePaste('patientId', true)}
                    />
                </Form.Item>

                <Form.Item label={t('Patient Name')} name='patientName'>
                    <Input onPaste={handlePaste('patientName', false)} />
                </Form.Item>

                <Form.Item label={t('Accession No')} name='accessionNumber'>
                    <Input onPaste={handlePaste('accessionNumber', false)} />
                </Form.Item>

                {showHospitalFilter && (
                    <Form.Item label={t('Hospitals')} name='hospitalIdList'>
                        <HospitalSelectBox
                            modalOnMobile
                            modalTitle={t('Hospitals')}
                            onData={(data) => {
                                if (data?.length > 0)
                                    setShowHospitalFilter(true);
                                else setShowHospitalFilter(false);
                            }}
                        />
                    </Form.Item>
                )}

                <Form.Item label={t('Modality')} name='modalities'>
                    <ModalitySelectBox
                        modalOnMobile
                        modalTitle={t('Modality')}
                    />
                </Form.Item>

                <Divider>
                    <div>
                        <Typography.Text
                            style={{ fontWeight: 'bold', marginRight: 8 }}
                        >
                            {t('Detail Filters')}{' '}
                        </Typography.Text>
                        <Switch checked={showDetail} onChange={(e) => setShowDetail(e)} />
                    </div>
                </Divider>

                <div
                    style={{
                        marginTop: 16,
                        display: showDetail ? '' : 'none',
                    }}
                >
                    {/* Çekim listesi filtresi. Liste izinlerinden hiçbiri olmayan kullanıcıda alan HİÇ
                        render edilmez (istek de atılmaz) — özelliği almayan için filtre çubuğu eskisi gibi. */}
                    {(hasPermission(PERMISSIONS.LIST_VIEW)
                        || hasPermission(PERMISSIONS.LIST_VIEW_ALL)
                        || hasPermission(PERMISSIONS.LIST_CREATE)
                        || hasPermission(PERMISSIONS.LIST_ITEM_ADD)) && (
                        <Form.Item
                            label={t('Lists')}
                            name='listIdList'
                        >
                            <StudyListSelect
                                modalOnMobile
                                modalTitle={t('Lists')}
                            />
                        </Form.Item>
                    )}

                    {hasPermission(PERMISSIONS.VIEW_STUDY_INSTITUTION) && (
                        <InstitutionSelect
                            hide={true}
                            modalOnMobile
                            modalTitle={t('Institution')}
                            formItem={{
                                label: t('Institution'),
                                name: 'institutions',
                            }}
                        />
                    )}

                    <Form.Item name={'idList'} label={t('Study Id List')}>
                        <Input
                            placeholder={t('Separate values with comma, space or new line')}
                            onPaste={handlePaste('idList', true)}
                        />
                    </Form.Item>

                    <Form.Item name={'studyInstanceUIDList'} label={t('Study UID')}>
                        <Input
                            placeholder={t('Separate values with comma, space or new line')}
                            onPaste={handlePaste('studyInstanceUIDList', true)}
                        />
                    </Form.Item>

                    <Form.Item label={t('Study Type')} name={'studyTypeList'}>
                        <Select mode={'multiple'}>
                            <Select.Option value={null}>
                                {t('All')}
                            </Select.Option>
                            <Select.Option value={1}>
                                {t('Routine')}
                            </Select.Option>
                            <Select.Option value={2}>
                                {t('Inpatient')}
                            </Select.Option>
                            <Select.Option value={3}>
                                {t('Emergency')}
                            </Select.Option>
                            <Select.Option value={4}>
                                {t('Comparative Examination')}
                            </Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item label={t('Study Status')} name={'studyStatus'}>
                        <Select mode={'multiple'}>
                            <Select.Option value={-1}>
                                {t('All')}
                            </Select.Option>
                            <Select.Option value={0}>
                                {t('Not Completed')}
                            </Select.Option>
                            <Select.Option value={1}>
                                {t('Timeout, Reports not confirmed')}
                            </Select.Option>
                            <Select.Option value={2}>
                                {t('Completed')}
                            </Select.Option>
                            <Select.Option value={3}>
                                {t('Reports not confirmed')}
                            </Select.Option>
                            <Select.Option value={3}>
                                {t('OTHER')}
                            </Select.Option>
                        </Select>
                    </Form.Item>

                    {hasPermission(PERMISSIONS.STUDY_SET_READ) && (
                        <Form.Item
                            label={t('Assigned (Doctor)')}
                            name={'assignDoctor'}
                        >
                            <Select
                                showSearch={true}
                                filterOption={(input, option) => matchesSearch(option?.children?.toString(), input)}
                            >
                                <Select.Option value={null}>
                                    {t('All')}
                                </Select.Option>
                                {/* Hızlı filtre çubuğu herkese "Serbest" ve "Serbest + Atanmış" seçtirir; panel de adıyla göstersin. */}
                                <Select.Option value={'-1'}>
                                    {t('Not Assigned')}
                                </Select.Option>
                                {user?.data?.id && (
                                    <Select.Option value={`-1,${user.data.id}`}>
                                        {t('QF_ASSIGN_FREE_OR_MINE')}
                                    </Select.Option>
                                )}
                                {!hasPermission(
                                    PERMISSIONS.STUDY_ASSIGN_DOCTOR_USER
                                ) && (
                                        <Select.Option value={user?.data?.id}>
                                            {t('Assigned Me')}
                                        </Select.Option>
                                    )}
                                {hasPermission(
                                    PERMISSIONS.STUDY_ASSIGN_DOCTOR_USER
                                ) && (
                                        <>
                                            {readableUsers.map((x) => (
                                                <Select.Option
                                                    value={x.id}
                                                    key={x.id}
                                                >
                                                    {x.visibleName}
                                                </Select.Option>
                                            ))}
                                        </>
                                    )}
                            </Select>
                        </Form.Item>
                    )}

                    {hasPermission(PERMISSIONS.REPORT_ADD_OR_EDIT) && (
                        <Form.Item
                            label={t('Assigned (Reporter)')}
                            name={'assignReporter'}
                        >
                            <Select
                                showSearch={true}
                                filterOption={(input, option) => matchesSearch(option?.children?.toString(), input)}
                            >
                                <Select.Option value={null}>
                                    {t('All')}
                                </Select.Option>
                                <Select.Option value={'-1'}>
                                    {t('Not Assigned')}
                                </Select.Option>
                                {user?.data?.id && (
                                    <Select.Option value={`-1,${user.data.id}`}>
                                        {t('QF_ASSIGN_FREE_OR_MINE')}
                                    </Select.Option>
                                )}
                                {!hasPermission(
                                    PERMISSIONS.STUDY_ASSIGN_REPORTER_USER
                                ) && (
                                        <Select.Option value={user?.data?.id}>
                                            {t('Assigned Me')}
                                        </Select.Option>
                                    )}
                                {hasPermission(
                                    PERMISSIONS.STUDY_ASSIGN_REPORTER_USER
                                ) && (
                                        <>
                                            {writableUsers.map((x) => (
                                                <Select.Option
                                                    value={x.id}
                                                    key={x.id}
                                                >
                                                    {x.visibleName}
                                                </Select.Option>
                                            ))}
                                        </>
                                    )}
                            </Select>
                        </Form.Item>
                    )}

                    <Form.Item label={t('Read User')} name='readUserId'>
                        <UserSelectBox type={'read'} modalOnMobile modalTitle={t('Read User')} />
                    </Form.Item>

                    <Form.Item label={t('Wrote User')} name='writeUserId'>
                        <UserSelectBox type={'write'} modalOnMobile modalTitle={t('Wrote User')} />
                    </Form.Item>

                    <Form.Item
                        label={t('Report Written')}
                        name={'isReportWritten'}
                    >
                        <Select>
                            <Select.Option value={null}>
                                {t('All')}
                            </Select.Option>
                            <Select.Option value={true}>
                                {t('Written')}
                            </Select.Option>
                            <Select.Option value={false}>
                                {t('Not Written')}
                            </Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={t('Voice Record')}
                        name={'hasVoiceRecord'}
                    >
                        <Select>
                            <Select.Option value={null}>
                                {t('All')}
                            </Select.Option>
                            <Select.Option value={true}>
                                {t('Has Voice Record')}
                            </Select.Option>
                            <Select.Option value={false}>
                                {t('No Voice Record')}
                            </Select.Option>
                        </Select>
                    </Form.Item>

                    {/* Yalnız hızlı filtre çubuğundan seçilir (açık görev tipi); gizli alan panelden "Ara" deyince düşmesin diye. */}
                    <Form.Item name={'openTaskType'} hidden>
                        <Input />
                    </Form.Item>

                    <Form.Item label={t('Is Read')} name={'isRead'}>
                        <Select>
                            <Select.Option value={null}>
                                {t('All')}
                            </Select.Option>
                            <Select.Option value={true}>
                                {t('Read')}
                            </Select.Option>
                            <Select.Option value={false}>
                                {t('Not Read')}
                            </Select.Option>
                        </Select>
                    </Form.Item>

                    {hasPermission(PERMISSIONS.SET_STUDY_REPORTABLE) && (
                        <Form.Item label={t('Is Reportable')} name={'isReportable'}>
                            <Select>
                                <Select.Option value={null}>
                                    {t('All')}
                                </Select.Option>
                                <Select.Option value={true}>
                                    {t('Reportable')}
                                </Select.Option>
                                <Select.Option value={false}>
                                    {t('Not Reportable')}
                                </Select.Option>
                            </Select>
                        </Form.Item>
                    )}

                    <Form.Item label={t('Open Task')} name={'hasOpenTask'}>
                        <Select>
                            <Select.Option value={null}>
                                {t('All')}
                            </Select.Option>
                            <Select.Option value={true}>
                                {t('Has Open Task')}
                            </Select.Option>
                            <Select.Option value={false}>
                                {t('No Task')}
                            </Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item label={t('AI Dicom')} name={'hasAiDicom'}>
                        <Select>
                            <Select.Option value={null}>
                                {t('All')}
                            </Select.Option>
                            <Select.Option value={true}>
                                {t('TRUE')}
                            </Select.Option>
                            <Select.Option value={false}>
                                {t('FALSE')}
                            </Select.Option>
                        </Select>
                    </Form.Item>

                    {hasPermission(PERMISSIONS.STUDY_LIST_DELETED) && (
                        <Form.Item label={t('Is Deleted')} name={'isDeleted'}>
                            <Select>
                                <Select.Option value={null}>
                                    {t('All')}
                                </Select.Option>
                                <Select.Option value={true}>
                                    {t('Deleted')}
                                </Select.Option>
                                <Select.Option value={false}>
                                    {t('Not Deleted')}
                                </Select.Option>
                            </Select>
                        </Form.Item>
                    )}

                    <Form.Item label={t('HL7 Filter')} name={'hl7FilterType'}>
                        <Select>
                            <Select.Option value={0}>
                                {t('All')}
                            </Select.Option>
                            <Select.Option value={1}>
                                {t('HAS_NO_HL7_MESSAGE')}
                            </Select.Option>
                        </Select>
                    </Form.Item>
                </div>
            </Form>
        </ConfigProvider>
    );
}
